import React, { useId, useMemo } from "react";
import useAddButtonLayer from "../hooks/useAddButtonLayer";

const getPlacementClasses = (strategy, orientation, placement) => {
  if (strategy === "inline") {
    return "relative flex items-center justify-center";
  }

  if (placement === "cover") {
    return "absolute inset-0 flex items-center justify-center";
  }

  if (orientation === "vertical") {
    return placement === "before"
      ? "absolute left-0 top-0 h-full -translate-x-1/2 flex items-center justify-center"
      : "absolute right-0 top-0 h-full translate-x-1/2 flex items-center justify-center";
  }

  if (placement === "after") {
    return "absolute bottom-0 left-0 w-full translate-y-1/2 flex items-center justify-center";
  }

  return "absolute top-0 left-0 w-full -translate-y-1/2 flex items-center justify-center";
};

const enhanceChild = (child, injectedProps) => {
  if (!React.isValidElement(child)) {
    return child;
  }
  return React.cloneElement(child, {
    ...child.props,
    ...injectedProps,
  });
};

const AddButtonOverlay = ({
  layer,
  overlayId,
  orientation,
  placement,
  shift,
  position,
  variant,
  className = "",
  strategy = "overlay",
  analyticsMeta,
  children,
  style,
}) => {
  const reactId = useId();
  const resolvedOverlayId = overlayId || `${layer}-${reactId}`;

  const overrides = useMemo(
    () => ({
      ...(orientation ? { orientation } : {}),
      ...(placement ? { placement } : {}),
      ...(variant ? { variant } : {}),
    }),
    [orientation, placement, variant],
  );

  const {
    config,
    wrapperZ,
    layerToken,
  } = useAddButtonLayer(resolvedOverlayId, {
    layerToken: layer,
    mode: "overlay",
    overrides,
    metadata: analyticsMeta,
  });

  const effectiveOrientation = orientation || config.orientation || "horizontal";
  const effectivePlacement = placement || config.placement || "before";
  const effectiveVariant = variant || config.variant || "component";

  const baseClasses = getPlacementClasses(strategy, effectiveOrientation, effectivePlacement);

  const childrenWithProps = React.Children.map(children, (child) =>
    enhanceChild(child, {
      layerId: resolvedOverlayId,
      layerToken,
      orientation: effectiveOrientation,
      variant: effectiveVariant,
      shift,
      position,
    }),
  );

  return (
    <div
      data-add-button-overlay={layer}
      data-add-button-layer-id={resolvedOverlayId}
      className={`${baseClasses} ${className}`.trim()}
      style={{ zIndex: wrapperZ, ...style }}
    >
      {childrenWithProps}
    </div>
  );
};

export default AddButtonOverlay;

